import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Lets a drag-sorted list be reordered from the keyboard. Spread the returned props onto each row's
 * drag handle:
 *
 *   const handleProps = useKeyboardReorder({ items, onMove: setItems, onCommit: saveOrder, getLabel: (it) => it.name });
 *   <span {...handleProps(it, index)} className="..."><GripVertical /></span>
 *
 * Tab reaches the handle. ArrowUp and ArrowDown move its row one place, Home and End to the top or the
 * bottom. The new order shows at once through `onMove`.
 *
 * `onCommit` saves it as soon as focus leaves the handle, after a pause if focus stays there, or when
 * the list goes away, whichever comes first; holding an arrow key therefore sends one save rather than
 * one for every step. Saving on the way out matters. The click that takes focus away may be the one
 * that reloads the list, switches its speciality or resets its order, and a save that was still
 * waiting would then have gone out against a different list. `onCommit` is handed the list as it
 * stands at that moment, so a drag made meanwhile is not undone by an older order.
 *
 * Focus stays on the handle of the row that moved: React can take a row out of the page and put it
 * back while reordering, which drops focus.
 *
 * The handle must not be a <button>. The lists give SortableJS `filter: 'button'`, so a button handle
 * could no longer be dragged; it is a focusable element with role="button" instead.
 */
const byHandle = (id) => {
  const value = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
  return document.querySelector(`[data-reorder-handle="${value}"]`);
};

export default function useKeyboardReorder({
  items,
  onMove,
  onCommit,
  getId = (item) => item.id,
  getLabel = () => 'item',
  delay = 1000,
}) {
  const itemsRef = useRef(items);
  const onCommitRef = useRef(onCommit);
  const focusId = useRef(null);
  const timer = useRef(null);
  const unsaved = useRef(false);
  itemsRef.current = items;
  onCommitRef.current = onCommit;

  // Saves the order as it stands, if a keyboard move has changed it since the last save.
  const commit = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!unsaved.current) return;
    unsaved.current = false;
    onCommitRef.current?.(itemsRef.current);
  }, []);

  useLayoutEffect(() => {
    if (focusId.current === null) return;
    const handle = byHandle(focusId.current);
    focusId.current = null;
    if (handle && document.activeElement !== handle) handle.focus();
  });

  // A save still waiting when the list unmounts is sent then.
  useEffect(() => commit, [commit]);

  return (item, index) => ({
    role: 'button',
    tabIndex: 0,
    'data-reorder-handle': String(getId(item)),
    'aria-label': `Move ${getLabel(item)}, position ${index + 1} of ${(items || []).length}`,
    title: 'Drag to reorder, or press the up and down arrow keys',
    onBlur: commit,
    onKeyDown: (e) => {
      const list = itemsRef.current || [];
      const from = list.findIndex((x) => getId(x) === getId(item));
      if (from < 0) return;
      let to;
      if (e.key === 'ArrowUp') to = from - 1;
      else if (e.key === 'ArrowDown') to = from + 1;
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = list.length - 1;
      else return;
      e.preventDefault();
      if (to < 0 || to >= list.length || to === from) return;
      const next = [...list];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      itemsRef.current = next;
      unsaved.current = true;
      focusId.current = String(getId(item));
      onMove(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(commit, delay);
    },
  });
}
